import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  query,
  where,
  onSnapshot,
  orderBy,
  Timestamp,
  getDocs,
  or,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '@/lib/firebase';
import type { Task, TaskAttachment } from '@/types';

// Collection reference
const tasksCollection = collection(db, 'tasks');

/**
 * Upload a file to Firebase Storage
 * @param file - The file to upload
 * @param userId - The user ID for file organization
 * @param taskId - Optional task ID for organizing files
 * @returns The attachment object with download URL
 */
export const uploadTaskAttachment = async (
  file: File,
  userId: string,
  taskId?: string
): Promise<TaskAttachment> => {
  try {
    // Generate unique filename with timestamp to prevent overwrites
    const timestamp = Date.now();
    const uniqueFilename = `${timestamp}-${file.name}`;
    
    // Create storage path: tasks/{userId}/{taskId or 'temp'}/{filename}
    const storagePath = `tasks/${userId}/${taskId || 'temp'}/${uniqueFilename}`;
    const storageRef = ref(storage, storagePath);
    
    // Upload file
    const uploadResult = await uploadBytes(storageRef, file);
    
    // Get download URL
    const downloadURL = await getDownloadURL(uploadResult.ref);
    
    // Return attachment object
    const attachment: TaskAttachment = {
      id: `att_${timestamp}_${Math.random()}_${file.name}`,
      name: file.name,
      type: file.type,
      size: file.size,
      url: downloadURL,
    };
    
    return attachment;
  } catch (error) {
    console.error('Error uploading file:', error);
    throw new Error(`Failed to upload file: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Delete a file from Firebase Storage
 * @param fileUrl - The download URL of the file to delete
 */
export const deleteTaskAttachment = async (fileUrl: string): Promise<void> => {
  try {
    // Skip if it's a base64 data URL (legacy attachments)
    if (fileUrl.startsWith('data:')) {
      return;
    }
    
    // Extract storage path from download URL
    // Firebase Storage URLs have format: https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{path}?...
    const url = new URL(fileUrl);
    const pathMatch = url.pathname.match(/\/o\/(.+)$/);
    
    if (!pathMatch) {
      console.warn('Could not extract storage path from URL:', fileUrl);
      return;
    }
    
    const storagePath = decodeURIComponent(pathMatch[1]);
    const fileRef = ref(storage, storagePath);
    
    await deleteObject(fileRef);
  } catch (error) {
    console.error('Error deleting file from storage:', error);
    // Don't throw - allow the task to be deleted even if file deletion fails
  }
};

/**
 * Delete all attachments for a task
 * @param attachments - Array of task attachments to delete
 */
export const deleteTaskAttachments = async (attachments: TaskAttachment[]): Promise<void> => {
  if (!attachments || attachments.length === 0) {
    return;
  }
  
  const deletePromises = attachments.map(attachment => {
    if (attachment.url) {
      return deleteTaskAttachment(attachment.url);
    }
    return Promise.resolve();
  });
  
  await Promise.all(deletePromises);
};

// Convert Firestore Timestamp to Date
const timestampToDate = (timestamp: unknown): Date => {
  if (timestamp instanceof Timestamp) {
    return timestamp.toDate();
  }
  if (timestamp instanceof Date) {
    return timestamp;
  }
  if (typeof timestamp === 'string') {
    return new Date(timestamp);
  }
  return new Date();
};

// Convert Task from Firestore format to app format
const convertFirestoreTask = (docData: Record<string, unknown>): Task => {
  return {
    ...docData,
    dueDate: timestampToDate(docData.dueDate),
    createdAt: timestampToDate(docData.createdAt),
  } as Task;
};

// Convert Task from app format to Firestore format
const convertTaskToFirestore = (task: Partial<Task> & { updatedAt?: Date }, isUpdate = false) => {
  const firestoreTask: Record<string, unknown> = { ...task };
  
  // Convert Date objects to Firestore Timestamps
  if (task.dueDate) {
    firestoreTask.dueDate = Timestamp.fromDate(task.dueDate instanceof Date ? task.dueDate : new Date(task.dueDate));
  }
  if (task.createdAt) {
    firestoreTask.createdAt = Timestamp.fromDate(task.createdAt instanceof Date ? task.createdAt : new Date(task.createdAt));
  }
  if (task.updatedAt) {
    firestoreTask.updatedAt = Timestamp.fromDate(task.updatedAt instanceof Date ? task.updatedAt : new Date(task.updatedAt));
  }
  if (task.originalDueDate) {
    firestoreTask.originalDueDate = Timestamp.fromDate(task.originalDueDate instanceof Date ? task.originalDueDate : new Date(task.originalDueDate));
  }
  if (task.lastSnoozedAt) {
    firestoreTask.lastSnoozedAt = Timestamp.fromDate(task.lastSnoozedAt instanceof Date ? task.lastSnoozedAt : new Date(task.lastSnoozedAt));
  }
  
  // Remove fields that shouldn't be in Firestore
  delete firestoreTask.id; // ID is the document key, not a field
  
  // For updates: Convert undefined fields to deleteField() so they're removed from Firestore
  // For creates: Remove undefined fields entirely
  if (isUpdate) {
    Object.keys(firestoreTask).forEach(key => {
      if (firestoreTask[key] === undefined) {
        firestoreTask[key] = deleteField();
      }
    });
  } else {
    // For new documents, remove undefined fields
    Object.keys(firestoreTask).forEach(key => {
      if (firestoreTask[key] === undefined) {
        delete firestoreTask[key];
      }
    });
  }
  
  return firestoreTask;
};

/**
 * Subscribe to user's tasks in real-time
 * Returns unsubscribe function
 */
export const subscribeToUserTasks = (
  userId: string,
  callback: (tasks: Task[]) => void,
  onError?: (error: Error) => void
): (() => void) => {
  try {
    // Query tasks where user is creator OR task is shared with them
    const q = query(
      tasksCollection,
      or(
        where('createdBy', '==', userId),
        where('sharedWith', 'array-contains', userId)
      ),
      orderBy('dueDate', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const tasks: Task[] = [];
        snapshot.forEach((doc) => {
          const taskData = doc.data();
          tasks.push(convertFirestoreTask({ ...taskData, id: doc.id }));
        });
        callback(tasks);
      },
      (error) => {
        console.error('Error subscribing to tasks:', error);
        if (onError) {
          onError(error);
        }
      }
    );

    return unsubscribe;
  } catch (error) {
    console.error('Error setting up task subscription:', error);
    if (onError && error instanceof Error) {
      onError(error);
    }
    return () => {}; // Return no-op unsubscribe function
  }
};

/**
 * Get all tasks for a user (one-time fetch)
 */
export const getUserTasks = async (userId: string): Promise<Task[]> => {
  try {
    const q = query(
      tasksCollection,
      or(
        where('createdBy', '==', userId),
        where('sharedWith', 'array-contains', userId)
      ),
      orderBy('dueDate', 'desc')
    );

    const snapshot = await getDocs(q);
    const tasks: Task[] = [];
    
    snapshot.forEach((doc) => {
      const taskData = doc.data();
      tasks.push(convertFirestoreTask({ ...taskData, id: doc.id }));
    });

    return tasks;
  } catch (error) {
    console.error('Error fetching user tasks:', error);
    throw error;
  }
};

/**
 * Create a new task
 */
export const createTask = async (task: Omit<Task, 'id'>): Promise<string> => {
  try {
    const now = new Date();
    const taskData = convertTaskToFirestore({
      ...task,
      createdAt: task.createdAt || now,
      updatedAt: now,
    } as Partial<Task> & { updatedAt: Date }, false); // false = not an update

    const docRef = await addDoc(tasksCollection, taskData);
    return docRef.id;
  } catch (error) {
    console.error('Error creating task:', error);
    throw error;
  }
};

/**
 * Update an existing task
 */
export const updateTask = async (taskId: string, updates: Partial<Task>): Promise<void> => {
  try {
    const taskRef = doc(db, 'tasks', taskId);
    const updateData = convertTaskToFirestore({
      ...updates,
      updatedAt: new Date(),
    } as Partial<Task> & { updatedAt: Date }, true); // true = this is an update

    await updateDoc(taskRef, updateData);
  } catch (error) {
    console.error('Error updating task:', error);
    throw error;
  }
};

/**
 * Get a single task by ID
 */
export const getTask = async (taskId: string): Promise<Task | null> => {
  try {
    const { getDoc } = await import('firebase/firestore');
    const taskRef = doc(db, 'tasks', taskId);
    const taskSnap = await getDoc(taskRef);
    
    if (!taskSnap.exists()) {
      return null;
    }
    
    return convertFirestoreTask({ id: taskSnap.id, ...taskSnap.data() });
  } catch (error) {
    console.error('Error getting task:', error);
    throw error;
  }
};

/**
 * Delete a task
 */
export const deleteTask = async (taskId: string): Promise<void> => {
  try {
    // First, get the task to retrieve its attachments
    const taskRef = doc(db, 'tasks', taskId);
    const taskDoc = await getDocs(query(tasksCollection, where('__name__', '==', taskId)));
    
    if (!taskDoc.empty) {
      const taskData = taskDoc.docs[0].data();
      const task = convertFirestoreTask({ ...taskData, id: taskDoc.docs[0].id });
      
      // Delete all attachments from storage
      if (task.attachments && task.attachments.length > 0) {
        await deleteTaskAttachments(task.attachments);
      }
    }
    
    // Delete the task document
    await deleteDoc(taskRef);
  } catch (error) {
    console.error('Error deleting task:', error);
    throw error;
  }
};

/**
 * Get all tasks (for notification rescheduling on app startup)
 */
export const getAllTasks = async (): Promise<Task[]> => {
  try {
    const q = query(tasksCollection, orderBy('dueDate', 'desc'));
    const snapshot = await getDocs(q);
    const tasks: Task[] = [];
    
    snapshot.forEach((doc) => {
      const taskData = doc.data();
      tasks.push(convertFirestoreTask({ ...taskData, id: doc.id }));
    });

    return tasks;
  } catch (error) {
    console.error('Error fetching all tasks:', error);
    return []; // Return empty array on error to prevent app crash
  }
};
